import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { PasskeyCancelled, passkeysSupported, signInWithPasskey } from '../auth/passkeys.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';
import { AuthLayout } from './AuthLayout.js';

export function SignInScreen(): ReactNode {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const registration = useQuery({
    queryKey: ['registration-status'],
    queryFn: () => api.get('/api/v1/auth/registration'),
    staleTime: 60_000,
  });

  const signIn = useMutation({
    mutationFn: () => api.post('/api/v1/auth/login', { email, password }),
    onSuccess: async (result) => {
      if (result.status === 'mfa_required') {
        await navigate({ to: '/mfa' });
        return;
      }
      await refresh();
      await navigate({ to: '/' });
    },
  });

  const passkey = useMutation({
    mutationFn: signInWithPasskey,
    onSuccess: async () => {
      await refresh();
      await navigate({ to: '/' });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    signIn.mutate();
  };

  // A dismissed platform prompt is not a failure and is not reported as one.
  const passkeyError =
    passkey.error === null || passkey.error instanceof PasskeyCancelled ? null : passkey.error;
  const error = signIn.error ?? passkeyError;

  return (
    <AuthLayout
      title={t('auth.signIn.title')}
      subtitle={t('auth.signIn.subtitle')}
      footer={
        registration.data?.open === true ? (
          <p>
            {t('auth.signIn.noAccount')} <a href="/register">{t('auth.signUp')}</a>
          </p>
        ) : (
          <p>{t('auth.registration.invitationOnly')}</p>
        )
      }
    >
      <form className="form" onSubmit={onSubmit} noValidate>
        {error === null || error === undefined ? null : (
          <Alert tone="critical">
            {/* Localised from the stable code, never from a server-authored sentence. */}
            {error instanceof ApiError ? t(`error.${error.code}`) : t('error.SERVICE_UNAVAILABLE')}
          </Alert>
        )}

        <TextField
          label={t('auth.email')}
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <TextField
          label={t('auth.password')}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <Button type="submit" variant="primary" loading={signIn.isPending}>
          {t('auth.signIn.submit')}
        </Button>

        {passkeysSupported() ? (
          <Button variant="secondary" loading={passkey.isPending} onClick={() => passkey.mutate()}>
            {t('passkey.signIn')}
          </Button>
        ) : null}

        <p className="form__aside">
          <a href="/forgot-password">{t('auth.forgotPassword')}</a>
        </p>
      </form>
    </AuthLayout>
  );
}
